import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { DeepPartial, useForm, UseFormReset } from 'react-hook-form';

import { ApiStatuses, NotificationPosition, Size } from 'coreOld/enums';
import { useNotification } from 'coreOld/hooks/useNotification';
import { isEmptyObject, isObject } from 'coreOld/utils';

import FormView from './FormView';

interface IProps<T> {
  isOpen: boolean;
  onClose: (reset) => void;
  title: string;
  subtitle?: string;
  submitBtnLabel?: string;
  removeBtnLabel?: string;
  defaultValues?: DeepPartial<T>;
  selectedItem?: DeepPartial<T>;
  allowRemove?: boolean;
  size?: Size;
  minWidth?: number;
  onSubmit: (
    reset: UseFormReset<T>,
    setError: Dispatch<SetStateAction<string>>
  ) => (data: T) => void;
  disabledSubmit?: boolean;
  onRemove?: (reset?: UseFormReset<T>) => void;
  hasGoBack?: boolean;
  goBack?: () => void;
  children?;
  schema?;
  loadedDefaultValues?: boolean;
  beforeSubmitContent?;
  footerContent?;
}

const MIN_WIDTH = 362;

const Form = <T,>({
  isOpen,
  onClose,
  title,
  subtitle,
  submitBtnLabel,
  removeBtnLabel,
  defaultValues,
  selectedItem,
  allowRemove = false,
  hasGoBack = false,
  size,
  minWidth = MIN_WIDTH,
  onSubmit,
  onRemove,
  goBack,
  disabledSubmit = false,
  children,
  schema,
  loadedDefaultValues = false,
  beforeSubmitContent,
  footerContent,
}: IProps<T>) => {
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const [error, setError] = useState('');
  const { clearModalNotification, showNotification } = useNotification();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
    getValues,
  } = useForm<T>({
    resolver: yupResolver(schema),
    defaultValues: selectedItem || defaultValues,
  });

  useEffect(() => {
    if (loadedDefaultValues && !isEmptyObject(defaultValues, true)) {
      reset(defaultValues);
    }
  }, [loadedDefaultValues]);

  useEffect(() => {
    if (isObject(selectedItem)) {
      reset(selectedItem);
    }
  }, [Object.values(selectedItem || {}).length]);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const errorsMessages: Record<string, any> = errors && Object.values(errors);

      const firstError: Record<string, any> = errorsMessages?.[0];

      showNotification({
        message: firstError?.message || Object.values(firstError)?.[0]?.message,
        type: ApiStatuses.ERROR,
        position: NotificationPosition.MODAL,
      });

      return;
    }

    clearModalNotification();
  }, [errors]);

  useEffect(() => {
    if (isObject(selectedItem)) {
      reset(selectedItem);

      return;
    }

    if (defaultValues) {
      reset(defaultValues);

      return;
    }

    reset();
  }, [isOpen]);

  const handleOnClose = (reset: UseFormReset<T>) => {
    clearModalNotification();
    reset();
    onClose(reset);
  };

  const handleGoBack = () => {
    clearModalNotification();
    reset();

    goBack && goBack();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <FormView<T>
      isOpen={isOpen}
      onClose={handleOnClose}
      onSubmit={onSubmit}
      onRemove={onRemove}
      goBack={goBack ? handleGoBack : undefined}
      disabledSubmit={disabledSubmit}
      submitBtnLabel={submitBtnLabel}
      removeBtnLabel={removeBtnLabel}
      selectedItem={selectedItem}
      allowRemove={allowRemove}
      hasGoBack={hasGoBack}
      title={title}
      subtitle={subtitle}
      register={register}
      handleSubmit={handleSubmit}
      schema={schema}
      errors={errors}
      setError={setError}
      reset={reset}
      // eslint-disable-next-line react/no-children-prop
      children={children}
      control={control}
      setValue={setValue}
      getValues={getValues}
      size={size}
      minWidth={minWidth}
      beforeSubmitContent={beforeSubmitContent}
      footerContent={footerContent}
    />
  );
};

export default Form;
